import React, { useState, useRef, useCallback, useEffect, useContext, Ref, useMemo } from "react";
import Paper from "@material-ui/core/Paper";
import Popper, { PopperPlacementType, PopperProps } from "@material-ui/core/Popper";
import CircularProgress from "@material-ui/core/CircularProgress";
import Fade from "@material-ui/core/Fade";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import { useTheme } from "@material-ui/core/styles";
import ClearIcon from "@material-ui/icons/Clear";
import { makeStyles } from "@material-ui/core/styles";
import DoneIcon from "@material-ui/icons/Done";
import SearchIcon from "@material-ui/icons/Search";
import EditIcon from "@material-ui/icons/Edit";
import { AutoFillIcon } from "../images";
import MUIAutocomplete, { AutocompleteProps, AutocompleteCloseReason } from "@material-ui/lab/Autocomplete";

import { colorsLight } from "../../themes";
import { TextField, TextFieldProps } from "../TextField";
import { AutocompleteChangeSubscriberContext } from "./autocompleteContext";
import {
  useStylesAutocomplete,
  defaultFilterOptions,
  defaultGetOptionLabel,
  defaultGetOptionSelected,
  useStylesLookupEndAdornment,
} from "./commonAutocomplete";
import LazyLoadingOptions, { LOAD_MORE_BATCH_SIZE, useLoadMoreItems } from "./LazyLoadingOptions";
import { SelectOptionsHook } from "./SelectOptionsHook";

const useStyles = makeStyles({
  noOptions: {
    display: "none",
  },
  optionKeepStyleWhenDisabled: {
    '&[aria-disabled="true"]': {
      opacity: 1,
    },
  },
});

type OnChangeParams<T, FreeSolo extends boolean> = Parameters<
  Required<AutocompleteProps<T, false, false, FreeSolo>>["onChange"]
>;
export interface SingleSelectPropsNew<T, FreeSolo extends boolean> {
  selectedValue: T | null;
  setSelectedValue: (option: OnChangeParams<T, FreeSolo>[1]) => void;
  useOptions: SelectOptionsHook<T>;
  useOptionsAdditionalParams?: object;
  reactLabel?: React.ReactNode;
  maxVisibleItems?: number;
  /** Label shown when there is no selected value */
  emptyLabel?: React.ReactNode;
  helperText?: React.ReactNode;
  advancedProviderFacilitySearch?: boolean;
  required?: boolean;
  /** @Deprecated */
  withIcon?: boolean;
  autoFilledIcon?: boolean;
  withEditAdornment?: boolean;
  withWarnAdornment?: boolean;
  withTooltipInfoAdornment?: boolean;
  endAdornmentRef?: Ref<HTMLDivElement>;
  TextFieldProps?: Partial<TextFieldProps>;
  editAction?: () => void;
  error?: boolean;
  clearable?: boolean;
  /** Optionally set a fixed width for the options list. By default this is the same size as the inline UI of the component. */
  optionsWidth?: number;
  /** If this is true (default) then we will render a checkmark in the options list next to the selected option
   * Note: This behavior is buggy as currently implemented. If we use a rest-hook to dynamically fetch new options based
   *       on a query (which is true in most use-cases) then the selected option comparison doesn't really work and we
   *       don't mark selected options. TODO We should create a variant that properly marks selections (and de-dupes for multi-select)
   */
  markSelectedOptions?: boolean;
  /**
   * Will not add adornment when set
   * Overrides all other adornment params
   */
  noAdornment?: boolean;
  /**
   * Will enter the disabled state when there is only one option
   */
  disableWhenOneOption?: boolean;

  disablePortal?: boolean;
  /** determines if the contents of this element will be displayed within LogRocket*/
  dataPublic?: boolean;
  /* This prop is used to hug the length of the options if it is not a lazy loaded API */
  isLazyLoaded?: boolean;
}

// Temporary export for storybook documentation
export function SingleSelectForPropsNew<T>(props: SingleSelectPropsNew<T, false>) {}

interface IProps<T, FreeSolo extends boolean>
  extends Omit<
      AutocompleteProps<T, false, false, FreeSolo>,
      "value" | "onChange" | "renderInput" | "options" | "disableClearable"
    >,
    SingleSelectPropsNew<T, FreeSolo> {}

export function SingleSelectForProviderFacilitySelect<T, FreeSolo extends boolean = false>({
  selectedValue,
  setSelectedValue,
  useOptions,
  useOptionsAdditionalParams,
  label,
  reactLabel,
  emptyLabel,
  helperText,
  autoFilledIcon,
  withEditAdornment,
  withWarnAdornment,
  withTooltipInfoAdornment,
  endAdornmentRef,
  TextFieldProps,
  editAction,
  error,
  // MuiAutocomplete doesn't pass through `placeholder` as advertised:
  // https://github.com/mui-org/material-ui/issues/21304
  placeholder,
  // props we will process manually
  className,
  renderOption,
  clearable,
  optionsWidth,
  markSelectedOptions = true,
  withIcon,
  noAdornment,
  required = false,
  disableWhenOneOption,
  advancedProviderFacilitySearch = false,
  maxVisibleItems,
  disablePortal = false,
  dataPublic = false,
  isLazyLoaded = true,
  disabled: forceDisabled,
  ...props
}: IProps<T, FreeSolo> & React.HTMLProps<HTMLDivElement>) {
  const classes = useStyles();
  const [isOpen, setIsOpen] = React.useState(false);

  // Autocomplete query is the search text the user has typed
  const [query, setQuery] = useState("");
  const isQueryStaleRef = useRef(false);
  const queryRef = useRef("");
  const handleInputChange = (event: any) => {
    const newQuery = event?.target?.value ?? "";
    setQuery(newQuery);
    queryRef.current = newQuery;
    isQueryStaleRef.current = true;
  };

  // Keep latest props in a ref
  const latestProps = useRef(props);
  useEffect(() => {
    latestProps.current = props;
  });
  // make a stable onError function to avoid unnecessary options fetches
  const onError = useCallback((error: any) => {
    latestProps.current.onError?.(error);
    setIsOpen(false);
  }, []);

  const {
    options = [],
    optionsLoading,
    filterOptions = defaultFilterOptions,
    refetch: refetchOptions,
    hasMoreOptions,
  } = useOptions({
    query: query || "",
    offset: 0,
    max: LOAD_MORE_BATCH_SIZE,
    onError,
    additionalQueryParameters: useOptionsAdditionalParams,
  });

  useEffect(() => {
    if (!optionsLoading) {
      isQueryStaleRef.current = false;
    }
  }, [optionsLoading]);

  const { withoutLabel, withLabelAndSelections, validationErrorColors, ...autocompleteClasses } = useStylesAutocomplete(
    {
      isLazyLoaded,
      markSelectedOptions,
    }
  );
  const extraClasses = !label ? `${withoutLabel}` : "";

  const optionClassName = `${autocompleteClasses.option} ${classes.optionKeepStyleWhenDisabled}`;

  const { onChange: onChangeFromContext } = useContext(AutocompleteChangeSubscriberContext);

  const loadMoreItems = useLoadMoreItems(query, refetchOptions);
  const onChange = useCallback(
    (...[event, value]: OnChangeParams<T, FreeSolo>) => {
      setSelectedValue(value);
      onChangeFromContext({
        fieldName: label || placeholder || props.name || props.id || "unknown",
        selectedValue: value,
      });
      event.stopPropagation();
    },
    [setSelectedValue, onChangeFromContext, label, placeholder, props.name, props.id]
  );

  const { spacing } = useTheme();
  const [focused, setFocused] = useState(TextFieldProps?.focused || false);
  const [hovered, setHovered] = useState(false);

  // when true, grays out the background and disables input. Also disables end adornments.
  const disabled = disableWhenOneOption && options?.length === 1 && !!selectedValue;

  // icon "order of preference" is loading -> clear -> warning -> edit -> model training -> tooltip -> search
  const showLoadingIcon = optionsLoading && isOpen && !disabled;
  const showClearIcon = !showLoadingIcon && clearable && !!selectedValue && (focused || hovered) && !disabled;
  const showEditIcon =
    !showLoadingIcon && withEditAdornment && Boolean(editAction) && !disabled && (focused || hovered);
  const showSearchIcon = !showLoadingIcon && !disabled;
  const notLoadingOrClearIcon = !optionsLoading && !showClearIcon && !disabled;
  const showWarningIcon = notLoadingOrClearIcon && withWarnAdornment && !disabled;
  const showModelTrainingIcon =
    notLoadingOrClearIcon && !showWarningIcon && !showEditIcon && autoFilledIcon && !disabled;

  const endAdornmentClasses = useStylesLookupEndAdornment();

  const [rightValue, setRightValue] = useState(80);
  useEffect(() => {
    setRightValue(showClearIcon ? 80 : 40);
  }, [showClearIcon]);
  const textFieldLabel = !selectedValue && !focused && emptyLabel ? emptyLabel : reactLabel ? reactLabel : label;
  const endAdornment = useMemo(
    () => (
      <>
        <Fade in={showLoadingIcon} style={{ transitionDelay: showLoadingIcon ? "75ms" : "0ms" }}>
          <CircularProgress className={endAdornmentClasses.loadingSpinner} color="inherit" size={20} />
        </Fade>
        <Fade in={showSearchIcon} style={{ transitionDelay: showSearchIcon ? "75ms" : "0ms" }}>
          <SearchIcon
            className={endAdornmentClasses.normalIcon}
            style={{ position: "absolute", color: "neutral.dark" }}
          />
        </Fade>
        <Fade in={showClearIcon} style={{ transitionDelay: showClearIcon ? "75ms" : "0ms" }}>
          <IconButton
            className={endAdornmentClasses.iconButton}
            aria-label="Clear"
            title="Clear"
            disableRipple
            onClick={(event) => {
              event.stopPropagation();
              onChange(event, null, "clear");
            }}
            style={{ position: "absolute", right: 40, color: "neutral.dark" }}
          >
            <ClearIcon />
          </IconButton>
        </Fade>
        <Fade in={showModelTrainingIcon} style={{ transitionDelay: showModelTrainingIcon ? "75ms" : "0ms" }}>
          <span className={endAdornmentClasses.autoFilledIcon}>
            <AutoFillIcon />
          </span>
        </Fade>
        <Fade in={showEditIcon} style={{ transitionDelay: showEditIcon ? "75ms" : "0ms" }}>
          <IconButton
            className={endAdornmentClasses.iconButton}
            disableRipple
            onClick={(event) => {
              event.stopPropagation();
              editAction?.();
            }}
            style={{ position: "absolute", right: rightValue, color: "neutral.dark" }}
          >
            <EditIcon />
          </IconButton>
        </Fade>
      </>
    ),
    [
      editAction,
      endAdornmentClasses.autoFilledIcon,
      endAdornmentClasses.iconButton,
      endAdornmentClasses.loadingSpinner,
      endAdornmentClasses.normalIcon,
      onChange,
      rightValue,
      showClearIcon,
      showEditIcon,
      showLoadingIcon,
      showModelTrainingIcon,
      showSearchIcon,
    ]
  );

  const stickyOptionRef = useRef<T | undefined>();
  const stickyOption = useMemo(() => {
    const safeOptions = Array.isArray(options) ? options : [];
    const found = safeOptions.find((o: any) => ["noResults", "someResults"].includes(o?.optionType));
    if (found) {
      stickyOptionRef.current = found;
    }
    return stickyOptionRef.current;
  }, [options]);

  const stickyBottomContent = useMemo(
    () =>
      advancedProviderFacilitySearch && stickyOption && renderOption
        ? renderOption(stickyOption as T, { selected: false, inputValue: query })
        : undefined,
    [advancedProviderFacilitySearch, stickyOption, renderOption, query]
  );

  const listboxOptions =
    advancedProviderFacilitySearch && stickyOption
      ? (Array.isArray(options) ? options : []).filter(
          (o: any) => !["noResults", "someResults"].includes(o?.optionType)
        )
      : Array.isArray(options)
      ? options
      : [];

  // custom popper to set options width separately from the input width
  const stickyBottomContentRef = useRef(stickyBottomContent);
  useEffect(() => {
    if (!optionsLoading) {
      stickyBottomContentRef.current = stickyBottomContent;
    }
  }, [stickyBottomContent, optionsLoading]);

  const popperComponent = useCallback(
    (popperProps: PopperProps) => (
      <Popper {...popperProps} placement="bottom-start" {...(optionsWidth ? { style: { width: optionsWidth } } : null)}>
        {typeof popperProps.children === "function"
          ? (popperProps.children as (props: { placement: PopperPlacementType }) => React.ReactNode)({
              placement: popperProps.placement ?? "bottom-start",
            })
          : popperProps.children}
      </Popper>
    ),
    [optionsWidth]
  );

  const PaperComponent = useCallback(
    ({ children }: { children?: React.ReactNode }) => (
      <Paper>
        {children}
        {advancedProviderFacilitySearch && stickyBottomContentRef.current && (
          <div
            style={{
              borderTop: "1px solid #e0e0e0",
              backgroundColor: "#fff",
              padding: "11px 16px",
              pointerEvents: "auto",
              cursor: "pointer",
            }}
            onMouseDown={(event) => {
              event.preventDefault();
              event.stopPropagation();
              if (stickyOptionRef.current) {
                onChange(event as any, stickyOptionRef.current as T, "select-option");
              }
            }}
          >
            {stickyBottomContentRef.current}
          </div>
        )}
      </Paper>
    ),
    [advancedProviderFacilitySearch, onChange]
  );

  return (
    <MUIAutocomplete
      disablePortal={disablePortal}
      disabled={disabled || forceDisabled}
      className={`${extraClasses} ${className}`}
      classes={{
        ...autocompleteClasses,
        option: optionClassName,
        ...(advancedProviderFacilitySearch && { noOptions: classes.noOptions }),
      }}
      disableClearable={!clearable}
      forcePopupIcon={false}
      open={
        advancedProviderFacilitySearch
          ? isOpen && (query?.length ?? 0) > 0 && !(optionsLoading && (options || []).length === 0)
          : isOpen && !(optionsLoading && (options || []).length === 0)
      }
      onOpen={() => setIsOpen(true)}
      onClose={(event, reason: AutocompleteCloseReason) => {
        setIsOpen(false);
        if (advancedProviderFacilitySearch && reason !== "toggleInput") {
          setQuery("");
          queryRef.current = "";
        }
      }}
      onInputChange={handleInputChange}
      options={advancedProviderFacilitySearch ? listboxOptions : options}
      filterOptions={filterOptions}
      getOptionSelected={defaultGetOptionSelected}
      getOptionLabel={defaultGetOptionLabel}
      ListboxComponent={
        isLazyLoaded
          ? (LazyLoadingOptions as unknown as React.ComponentType<React.HTMLAttributes<HTMLElement>>)
          : undefined
      }
      ListboxProps={{
        numOptions: advancedProviderFacilitySearch ? listboxOptions.length : (options || []).length,
        loadMoreItems,
        isNextPageLoading: optionsLoading,
        hasNextPage: advancedProviderFacilitySearch
          ? Boolean(hasMoreOptions)
          : Boolean(hasMoreOptions) &&
            (options || []).filter((o: any) => !["noResults", "someResults"].includes(o?.optionType)).length > 0,
        markSelectedOptions,
        query,
        ...(advancedProviderFacilitySearch && maxVisibleItems && { maxVisibleItems }),
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          required={required}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onMouseEnter={() => {
            if (advancedProviderFacilitySearch) {
              return;
            }
            setHovered(true);
          }}
          onMouseLeave={() => {
            if (advancedProviderFacilitySearch) {
              return;
            }
            setHovered(false);
          }}
          label={textFieldLabel}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          InputProps={{
            ...params.InputProps,
            endAdornment: !noAdornment ? (
              <InputAdornment ref={endAdornmentRef} position="end" className={endAdornmentClasses.endAdornment}>
                {endAdornment}
              </InputAdornment>
            ) : undefined,
          }}
          dataPublic={dataPublic}
          {...TextFieldProps}
        />
      )}
      renderOption={(option, state) => (
        <>
          {markSelectedOptions && state.selected && (
            <DoneIcon style={{ margin: spacing("auto", 1, "auto", 0), color: colorsLight.primary.main }} />
          )}
          {(renderOption && renderOption(option, state)) || (props.getOptionLabel || defaultGetOptionLabel)(option)}
        </>
      )}
      {
        ...props
        /* value and onChange must be passed after passthrough props in order to satisfy the type inference gods */
      }
      value={selectedValue}
      onChange={onChange}
      /* To customise options popper width https://github.com/mui-org/material-ui/issues/19376 */
      PopperComponent={popperComponent}
      {...(advancedProviderFacilitySearch && { PaperComponent })}
    />
  );
}
